import type { ReactNode } from 'react';
import type { IconName } from '@components/Icon/BaseIcon';
import * as tokens from '@tokens';
import {
  Header,
  HeaderTextContainer,
  StyledLink,
  HeaderText,
  HeaderChildrenContainer,
  StyledIcon,
  HeaderSubtitle,
} from './styles';

export interface CardHeaderProps {
  /**
   * Styles override.
   */
  className?: string;
  /**
   * Link for the title of the Header.
   */
  href?: string;
  /**
   * Callback for the title fo the Header.
   */
  onClick?: () => void;
  /**
   * Subtitle of the Header.
   */
  subtitle?: string;
  /**
   * Title of the Header.
   */
  title: string;
  /**
   * Children prop from React. To be used mainly for actions, renders at the right of the Header.
   */
  children?: ReactNode;
  /**
   * text-transform style.
   */
  capitalized?: boolean;
  /**
   * Icon of the header.
   */
  icon?: IconName;
  /**
   * Variant of the header.
   */
  variant?: 'l' | 'm';
}

export const CardHeader = ({
  className,
  title,
  subtitle,
  href,
  onClick,
  children,
  capitalized = true,
  icon,
  variant = 'm',
}: CardHeaderProps): JSX.Element => {
  return (
    <Header variant={variant} className={className} data-testid="card-header">
      {icon ? (
        <StyledIcon
          icon={icon}
          size="l"
          css={{ marginRight: tokens.spacer.x2 }}
          data-testid="card-header-icon"
        />
      ) : null}
      <HeaderTextContainer>
        {href || onClick ? (
          <StyledLink
            as={href ? 'a' : 'button'}
            kind="standalone"
            href={href}
            onClick={onClick}
            variant={variant}
          >
            {title}
          </StyledLink>
        ) : (
          <HeaderText
            as="span"
            textType="subtitle1"
            textColor="text_default"
            capitalized={capitalized}
            variant={variant}
          >
            {title}
          </HeaderText>
        )}
        {subtitle ? (
          <HeaderSubtitle
            role="note"
            as="span"
            textColor="text_light"
            capitalized={capitalized}
            variant={variant}
          >
            {subtitle}
          </HeaderSubtitle>
        ) : null}
      </HeaderTextContainer>
      {children && (
        <HeaderChildrenContainer>{children}</HeaderChildrenContainer>
      )}
    </Header>
  );
};
